import type { ReasonNode, Session } from './types'

export const CONNECTION_SELECTION_RULE =
  'Rotate generation branches in stored order, then take the next retained node in each branch. Model self-ratings do not rank selection.'

/** The caller owns eligibility. This projection allocates bounded context
 * without treating a model's confidence or novelty as measured usefulness. */
export function selectConnectionCandidates(
  session: Session,
  candidates: readonly ReasonNode[],
  limit: number,
): ReasonNode[] {
  if (!Number.isFinite(limit) || limit <= 0) return []
  const roots = new Map<string, string>()
  const branchOf = (start: string): string => {
    const path: string[] = []
    const seen = new Set<string>()
    let id = start
    let branch = start
    while (true) {
      if (roots.has(id)) {
        branch = roots.get(id)!
        break
      }
      if (seen.has(id)) {
        branch = [...seen].sort()[0]!
        break
      }
      seen.add(id)
      path.push(id)
      const parent = session.nodes[id]?.parentId
      if (!parent || !session.nodes[parent] || session.nodes[parent]!.parentId === null) {
        branch = id
        break
      }
      id = parent
    }
    for (const part of path) roots.set(part, branch)
    return branch
  }
  const branches = new Map<string, ReasonNode[]>()
  const seen = new Set<string>()
  for (const node of candidates) {
    if (seen.has(node.id)) continue
    seen.add(node.id)
    const branch = branchOf(node.id)
    const rows = branches.get(branch) ?? []
    rows.push(node)
    branches.set(branch, rows)
  }
  const result: ReasonNode[] = []
  const ceiling = Math.min(Math.floor(limit), seen.size)
  for (let depth = 0; result.length < ceiling; depth++) {
    for (const rows of branches.values()) {
      if (rows[depth]) result.push(rows[depth]!)
      if (result.length === ceiling) break
    }
  }
  return result
}
